import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { ESTIMATE_TYPE_OPTIONS, FORMULE_OPTIONS } from "@/constants/business";
import { useDialogs } from "@/context/DialogsContext";
import FileUploadField from "@/components/forms/FileUploadField";
import DatePickerField from "@/components/forms/DatePickerField";
import FormProgress from "@/components/forms/FormProgress";

const OUI_NON = ["oui", "non", "je ne sais pas"];
const CONTACT_OPTIONS = ["Téléphone", "WhatsApp", "Email"];

const INITIAL = {
  type_demande: "", formule: "", adresse_depart: "", adresse_arrivee: "", date_souhaitee: null,
  objets: "", etage_depart: "", etage_arrivee: "", ascenseur_depart: "", ascenseur_arrivee: "",
  aide_presente: "", commentaires: "",
  name: "", phone: "", email: "", contact_prefere: "", consent: false, website: "",
};

function Label({ children }) {
  return <p className="text-sm font-semibold text-navy mb-1.5">{children}</p>;
}

export default function QuickEstimateSection() {
  const { estimatePrefill } = useDialogs();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState("");
  const [source, setSource] = useState("");
  const total = 3;

  const set = (key) => (value) => setData((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!estimatePrefill) return;
    setSubmitted(false);
    if (estimatePrefill.source) setSource(estimatePrefill.source);
    setData((prev) => ({
      ...prev,
      ...(estimatePrefill.type ? { type_demande: estimatePrefill.type } : {}),
      ...(estimatePrefill.formule ? { formule: estimatePrefill.formule } : {}),
      ...(estimatePrefill.objets
        ? { objets: prev.objets ? `${prev.objets}\n${estimatePrefill.objets}` : estimatePrefill.objets }
        : {}),
    }));
  }, [estimatePrefill]);

  const next = () => {
    if (step === 1) {
      if (!data.type_demande) {
        toast.error("Merci de choisir un type de besoin.");
        return;
      }
      if (!data.adresse_depart || !data.adresse_arrivee) {
        toast.error("Merci d'indiquer l'adresse de départ et l'adresse d'arrivée.");
        return;
      }
    }
    setStep((s) => Math.min(total, s + 1));
  };

  const submit = async () => {
    if (!data.name) {
      toast.error("Merci d'indiquer votre prénom ou votre nom.");
      return;
    }
    if (!data.phone) {
      toast.error("Le téléphone est obligatoire pour vous recontacter.");
      return;
    }
    if (!data.consent) {
      toast.error("Merci d'accepter le consentement pour envoyer votre demande.");
      return;
    }
    if (data.website) {
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/leads", {
        lead_type: "devis_particulier",
        name: data.name,
        phone: data.phone,
        email: data.email || null,
        message: data.commentaires || null,
        details: {
          source_form: "estimation_rapide",
          page: window.location.pathname,
          source_bouton: source || "formulaire-estimation",
          type_demande: data.type_demande,
          formule: data.formule,
          adresse_depart: data.adresse_depart,
          adresse_arrivee: data.adresse_arrivee,
          date_souhaitee: data.date_souhaitee ? data.date_souhaitee.toISOString().slice(0, 10) : "",
          objets: data.objets,
          etage_depart: data.etage_depart,
          etage_arrivee: data.etage_arrivee,
          ascenseur_depart: data.ascenseur_depart,
          ascenseur_arrivee: data.ascenseur_arrivee,
          aide_presente: data.aide_presente,
          contact_prefere: data.contact_prefere,
        },
        files: files.map((f) => f.path),
        consent: data.consent,
      });
      const id = res.data?.id || "";
      setReference(res.data?.dossier_number || (id ? `ED-${id.slice(-6).toUpperCase()}` : ""));
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimation" data-testid="quick-estimate-section" className="py-14 md:py-20 px-4 md:px-8 bg-graylight border-b border-gray-200">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight text-navy mb-3">
            Obtenez une première estimation rapidement
          </h2>
          <p className="text-muted-foreground text-sm md:text-base">
            Décrivez votre besoin en moins de deux minutes. Nous vous recontactons avec une proposition adaptée.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-8">
          {submitted ? (
            <div className="text-center py-8" data-testid="estimate-success">
              <CheckCircle2 className="w-14 h-14 text-orange mx-auto mb-4" />
              <h3 className="font-heading font-bold text-2xl text-navy mb-2">Demande transmise</h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto">
                Votre demande a bien été transmise à Express Delivery.
                {reference && (
                  <> Votre numéro de dossier est <strong className="text-navy">{reference}</strong>.</>
                )}{" "}
                Nous allons étudier le trajet, le volume, les accès et les moyens nécessaires avant de revenir vers vous.
              </p>
              {reference && (
                <p className="text-xs font-semibold text-navy mt-4" data-testid="estimate-reference">
                  Numéro de dossier : {reference}
                </p>
              )}
              <a
                href={`https://wa.me/33766924257?text=${encodeURIComponent(
                  `Bonjour Express Delivery, je viens d'envoyer ma demande${reference ? ` (dossier ${reference})` : ""}.\n\nType de prestation : ${data.type_demande}\nFormule souhaitée : ${data.formule || "à définir"}\nDépart : ${data.adresse_depart}\nArrivée : ${data.adresse_arrivee}\nDate : ${data.date_souhaitee ? data.date_souhaitee.toISOString().slice(0, 10) : "à définir"}\nObjets : ${data.objets || "voir demande"}\nÉtage départ : ${data.etage_depart || "-"}\nÉtage arrivée : ${data.etage_arrivee || "-"}\nAide disponible : ${data.aide_presente || "-"}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="estimate-success-whatsapp"
                className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-white bg-whatsapp rounded-full px-6 py-3 hover:brightness-95 transition-all"
              >
                Poursuivre sur WhatsApp
              </a>
            </div>
          ) : (
            <>
              <FormProgress step={step} total={total} />
              <p className="text-xs text-muted-foreground font-semibold mt-2 mb-5">Étape {step} sur {total}</p>

              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }}>
                  {step === 1 && (
                    <div className="space-y-4">
                      <div>
                        <Label>Type de besoin *</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="estimate-type-options">
                          {ESTIMATE_TYPE_OPTIONS.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              data-testid={`estimate-type-${opt}`}
                              onClick={() => set("type_demande")(opt)}
                              className={`text-xs sm:text-[13px] font-semibold rounded-lg border px-2.5 py-2.5 text-center transition-colors ${
                                data.type_demande === opt
                                  ? "border-orange bg-orange/5 text-orange"
                                  : "border-gray-200 text-navy/75 hover:border-navy/40"
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Formule souhaitée</Label>
                        <Select value={data.formule} onValueChange={set("formule")}>
                          <SelectTrigger data-testid="estimate-formule" className="rounded-xl h-11"><SelectValue placeholder="Choisir une formule (optionnel)" /></SelectTrigger>
                          <SelectContent>
                            {FORMULE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            <SelectItem value="Je ne sais pas encore">Je ne sais pas encore</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Ville ou adresse de départ *</Label>
                          <Input data-testid="estimate-depart" placeholder="ex : Le Havre" value={data.adresse_depart} onChange={(e) => set("adresse_depart")(e.target.value)} className="rounded-xl h-11" />
                        </div>
                        <div>
                          <Label>Ville ou adresse d'arrivée *</Label>
                          <Input data-testid="estimate-arrivee" placeholder="ex : Rouen" value={data.adresse_arrivee} onChange={(e) => set("adresse_arrivee")(e.target.value)} className="rounded-xl h-11" />
                        </div>
                      </div>
                      <div>
                        <Label>Date souhaitée</Label>
                        <DatePickerField testId="estimate-date" value={data.date_souhaitee} onChange={set("date_souhaitee")} />
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <div>
                        <Label>Liste des objets</Label>
                        <Textarea data-testid="estimate-objets" placeholder="ex : 1 canapé 3 places, 1 lave-linge, 10 cartons…" value={data.objets} onChange={(e) => set("objets")(e.target.value)} className="rounded-xl" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Étage au départ</Label>
                          <Input data-testid="estimate-etage-depart" inputMode="numeric" placeholder="ex : 2" value={data.etage_depart} onChange={(e) => set("etage_depart")(e.target.value)} className="rounded-xl h-11" />
                        </div>
                        <div>
                          <Label>Ascenseur au départ</Label>
                          <Select value={data.ascenseur_depart} onValueChange={set("ascenseur_depart")}>
                            <SelectTrigger data-testid="estimate-ascenseur-depart" className="rounded-xl h-11"><SelectValue placeholder="oui / non" /></SelectTrigger>
                            <SelectContent>{OUI_NON.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Étage à l'arrivée</Label>
                          <Input data-testid="estimate-etage-arrivee" inputMode="numeric" placeholder="ex : RDC" value={data.etage_arrivee} onChange={(e) => set("etage_arrivee")(e.target.value)} className="rounded-xl h-11" />
                        </div>
                        <div>
                          <Label>Ascenseur à l'arrivée</Label>
                          <Select value={data.ascenseur_arrivee} onValueChange={set("ascenseur_arrivee")}>
                            <SelectTrigger data-testid="estimate-ascenseur-arrivee" className="rounded-xl h-11"><SelectValue placeholder="oui / non" /></SelectTrigger>
                            <SelectContent>{OUI_NON.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Aide disponible sur place</Label>
                        <Select value={data.aide_presente} onValueChange={set("aide_presente")}>
                          <SelectTrigger data-testid="estimate-aide" className="rounded-xl h-11"><SelectValue placeholder="oui / non" /></SelectTrigger>
                          <SelectContent>{OUI_NON.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Photos (facultatives mais recommandées)</Label>
                        <FileUploadField files={files} onChange={setFiles} testId="estimate-file-upload" />
                      </div>
                      <div>
                        <Label>Commentaire (facultatif)</Label>
                        <Textarea data-testid="estimate-commentaires" placeholder="Précisions utiles : objets fragiles, accès difficile…" value={data.commentaires} onChange={(e) => set("commentaires")(e.target.value)} className="rounded-xl" />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <div className="bg-graylight rounded-xl p-4" data-testid="estimate-recap">
                        <p className="font-heading font-semibold text-sm text-navy mb-2">Récapitulatif de votre demande</p>
                        <ul className="text-xs text-navy/75 space-y-1">
                          <li><span className="font-semibold">Besoin :</span> {data.type_demande}{data.formule ? ` — ${data.formule}` : ""}</li>
                          <li><span className="font-semibold">Trajet :</span> {data.adresse_depart} → {data.adresse_arrivee}{data.date_souhaitee ? ` • ${format(data.date_souhaitee, "d MMMM yyyy", { locale: fr })}` : ""}</li>
                          {data.objets && <li><span className="font-semibold">Objets :</span> {data.objets}</li>}
                          {(data.etage_depart || data.etage_arrivee) && (
                            <li><span className="font-semibold">Étages :</span> départ {data.etage_depart || "—"}{data.ascenseur_depart ? ` (ascenseur : ${data.ascenseur_depart})` : ""} • arrivée {data.etage_arrivee || "—"}{data.ascenseur_arrivee ? ` (ascenseur : ${data.ascenseur_arrivee})` : ""}</li>
                          )}
                          {files.length > 0 && <li><span className="font-semibold">Photos :</span> {files.length} fichier(s)</li>}
                        </ul>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Prénom ou nom *</Label>
                          <Input data-testid="estimate-name" autoComplete="name" value={data.name} onChange={(e) => set("name")(e.target.value)} className="rounded-xl h-11" />
                        </div>
                        <div>
                          <Label>Téléphone *</Label>
                          <Input data-testid="estimate-phone" type="tel" inputMode="tel" autoComplete="tel" value={data.phone} onChange={(e) => set("phone")(e.target.value)} className="rounded-xl h-11" />
                        </div>
                      </div>
                      <div>
                        <Label>Moyen de contact préféré</Label>
                        <div className="flex gap-2" data-testid="estimate-contact-options">
                          {CONTACT_OPTIONS.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              data-testid={`estimate-contact-${opt}`}
                              onClick={() => set("contact_prefere")(opt)}
                              className={`flex-1 text-sm font-semibold rounded-lg border px-3 py-2.5 transition-colors ${
                                data.contact_prefere === opt
                                  ? "border-orange bg-orange/5 text-orange"
                                  : "border-gray-200 text-navy/75 hover:border-navy/40"
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Email (facultatif)</Label>
                        <Input data-testid="estimate-email" type="email" inputMode="email" autoComplete="email" value={data.email} onChange={(e) => set("email")(e.target.value)} className="rounded-xl h-11" />
                      </div>
                      <input
                        type="text"
                        name="website"
                        value={data.website}
                        onChange={(e) => set("website")(e.target.value)}
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />
                      <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                        <Checkbox data-testid="estimate-consent" checked={data.consent} onCheckedChange={(v) => set("consent")(!!v)} className="mt-0.5" />
                        J'accepte que mes informations soient utilisées uniquement pour traiter ma demande, conformément à la politique de confidentialité. *
                      </label>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="flex justify-between items-center mt-7">
                <Button data-testid="estimate-back" variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="text-navy font-semibold">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                </Button>
                {step < total ? (
                  <Button data-testid="estimate-next" onClick={next} className="bg-navy hover:bg-navy/90 text-white rounded-full px-7 font-semibold">
                    Continuer <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button data-testid="estimate-submit" onClick={submit} disabled={submitting} className="bg-orange hover:bg-orange-hover text-white rounded-full px-7 font-semibold">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Recevoir mon estimation
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
